import {
  ESTIMATE_FORM_DEFAULT_VALUES,
  EstimateFormGroup,
  estimateFormSchema,
  type EstimateFormType,
  EstimateTypeSelector,
  useEstimateForm,
} from '@massilia/features'
import {
  type Passenger,
  useCreateEstimateMutation,
  useGetMyPassengersQuery,
  useGetMyProfileQuery,
} from '@massilia/queries'
import { Button } from '@massilia/ui'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'

import { useAuthStore } from '@/stores/auth.store'

export function EstimateForm() {
  const navigate = useNavigate()
  const createMutation = useCreateEstimateMutation()
  const { isAuthenticated, user } = useAuthStore()

  const { data: profile } = useGetMyProfileQuery(isAuthenticated)
  const { data: myPassengers } = useGetMyPassengersQuery(isAuthenticated)

  const phoneNumber =
    profile?.client?.phoneNumbers?.find((p) => p.type === 'PRINCIPAL')?.number ??
    profile?.client?.phoneNumbers?.[0]?.number ??
    ''

  const form = useEstimateForm({
    defaultValues: {
      ...ESTIMATE_FORM_DEFAULT_VALUES,
      ...(isAuthenticated && user
        ? {
            clientFirstName: user.firstName,
            clientLastName: user.lastName,
            contactEmail: user.email,
          }
        : {}),
    } satisfies EstimateFormType,
    onSubmit: async (value) => {
      const parsed = estimateFormSchema.safeParse(value)

      if (!parsed.success) {
        toast.error('Formulaire invalide - Veuillez vérifier les champs')
        return
      }

      createMutation.mutate(parsed.data, {
        onError: () => {
          toast.error('❌ Une erreur est survenue', {
            description: 'Impossible de créer le devis',
          })
        },
        onSuccess: () => {
          navigate({ to: '/devis-envoye' })
        },
      })
    },
  })

  // Sync phone number once profile loads (async)
  const phoneNumberSynced = useRef(false)
  useEffect(() => {
    if (phoneNumber && isAuthenticated && !phoneNumberSynced.current) {
      form.setFieldValue('contactPhoneNumber', phoneNumber)
      phoneNumberSynced.current = true
    }
  }, [phoneNumber, isAuthenticated, form])

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <EstimateTypeSelector
        fields={{
          estimateType: 'estimateType',
          type: 'type',
        }}
        form={form}
      />

      <EstimateFormGroup
        readOnlyClient={isAuthenticated}
        clientPassengers={myPassengers as (Passenger & { id: string })[] | undefined}
        onIAmTraveler={() => {
          const { clientFirstName, clientLastName } = form.state.values
          const passengers = form.state.values.newPassengers ?? []
          if (passengers.length === 0) return
          form.setFieldValue('newPassengers[0].firstName', clientFirstName)
          form.setFieldValue('newPassengers[0].lastName', clientLastName)
        }}
        fields={{
          clientFirstName: 'clientFirstName',
          clientLastName: 'clientLastName',
          contactEmail: 'contactEmail',
          approximativeBudget: 'approximativeBudget',
          carLoan: 'carLoan',
          companyPreference: 'companyPreference',
          complementaryInformation: 'complementaryInformation',
          contactPhoneNumber: 'contactPhoneNumber',
          departure: 'departure',
          departureDate: 'departureDate',
          departureFlexibilityAfter: 'departureFlexibilityAfter',
          departureFlexibilityBefore: 'departureFlexibilityBefore',
          destination: 'destination',
          estimateType: 'estimateType',
          existingPassengerIds: 'existingPassengerIds',
          flights: 'flights',
          hotelMinimumNumberOfStars: 'hotelMinimumNumberOfStars',
          maxStayDuration: 'maxStayDuration',
          newPassengers: 'newPassengers',
          onSiteActivities: 'onSiteActivities',
          particularities: 'particularities',
          pension: 'pension',
          returnDate: 'returnDate',
          returnFlexibilityAfter: 'returnFlexibilityAfter',
          returnFlexibilityBefore: 'returnFlexibilityBefore',
          transfertFromAirport: 'transfertFromAirport',
          travelClass: 'travelClass',
          type: 'type',
        }}
        form={form}
      />

      <div className="flex justify-between gap-4 pt-4">
        <Button type="button" variant="ghost" onClick={() => navigate({ to: '/recherche' })}>
          Retour
        </Button>
        <Button type="submit">Envoyer la demande</Button>
      </div>
    </form>
  )
}
